/** Case and whitespace are presentation, not meaning: every predicate here compares against this form. */
export const normaliseClinicalCode = (code: string): string => code.trim().toUpperCase();

/**
 * The 3-character category a code belongs to — `C34.1` and `C341` both yield `C34`. Both
 * classifications use "category" for this level; it is what their range notes are written against.
 */
export const clinicalCodeCategory = (code: string): string => normaliseClinicalCode(code).slice(0, 3);

/** The chapter letter a code belongs to — `Z27.6` yields `Z`. */
export const clinicalCodeChapter = (code: string): string => normaliseClinicalCode(code).slice(0, 1);

/**
 * Whether a code falls in an inclusive category range, the form both classifications state their
 * blocks in (`D00–D48`, `E49–E51`). A category whose digits are not a two-digit number is outside
 * every range rather than an error, so a malformed code classifies as "not in the group".
 */
export const isCategoryInRange = (code: string, chapter: string, min: number, max: number): boolean => {
  const category = clinicalCodeCategory(code);
  if (category.slice(0, 1) !== chapter.toUpperCase()) return false;

  const digits = category.slice(1);
  if (!/^\d\d$/.test(digits)) return false;

  const categoryNumber = Number.parseInt(digits, 10);
  return categoryNumber >= min && categoryNumber <= max;
};

/**
 * The dotted form both classifications print: `K250` becomes `K25.0`. Idempotent — a code that
 * already carries a dot is returned as-is, including a misplaced one, because inserting a second
 * would turn a correctable typo into nonsense (`K2.50` → `K2..50`).
 *
 * Case is left alone: a caller matching against rows that already exist must not change it, and the
 * reference tables are not guaranteed uppercase. Compose with {@link normaliseClinicalCode} when the
 * code is being normalised *for storage* rather than looked up.
 *
 * Only for a source that prints codes undotted. A feed whose undotted form is a *rejection* signal
 * needs its own guard first; see `normalizeSactCodeStyle`.
 */
export const dottedClinicalCode = (code: string): string => {
  const trimmed = code.trim();
  if (trimmed.includes(".") || trimmed.length <= 3) return trimmed;

  return `${trimmed.slice(0, 3)}.${trimmed.slice(3)}`;
};

/**
 * The single form an *exact* code comparison must be made in, so ` g459 `, `G459` and `G45.9` all
 * compare equal. {@link normaliseClinicalCode} alone leaves an undotted code undotted, which is a
 * silent miss against any set of dotted literals.
 */
export const canonicalClinicalCode = (code: string): string => normaliseClinicalCode(dottedClinicalCode(code));

/**
 * Whether a code falls in an inclusive range written with dotted endpoints (`G80.0–G80.2`). The code
 * is cut to the endpoint's length first, so a further-subdivided `G80.15` still sits in the range.
 * Whole-category ranges (`D00–D48`) belong to {@link isCategoryInRange}.
 */
export const isCodeInDottedRange = (code: string, from: string, to: string): boolean => {
  const truncated = canonicalClinicalCode(code).slice(0, to.length);

  return truncated >= from && truncated <= to;
};
